import moment from "moment";

import { IProgressAuditTrace } from "../../../features/progressAudit/progressAuditTracesSlice";
import { tracesOverlap } from "./traceOverlapUtils";
import { hasIntraTraceRegression, hasInterTraceRegression, findPreviousTraceForSameItem } from "./traceRegressionUtils";

export type TraceFindingSeverity = "error" | "warning";

export interface ITraceFinding {
  severity: TraceFindingSeverity;
  message: string;
}

const timeLabel = (trace: IProgressAuditTrace) => moment(trace.receivedAt).format("HH:mm:ss");

// A quick, scannable summary of what's wrong in a trace history, so it
// doesn't have to be read box by box - overlapping executions, requests
// that threw, and requests that never finished.
export const getTraceFindings = (traces: IProgressAuditTrace[]): ITraceFinding[] => {
  const findings: ITraceFinding[] = [];

  traces.forEach((trace, index) => {
    const nextTrace = traces[index + 1];
    if (nextTrace && tracesOverlap(trace, nextTrace)) {
      findings.push({
        severity: "error",
        message: `Solapamiento entre las trazas de ${timeLabel(trace)} y ${timeLabel(nextTrace)}`,
      });
    }
    if (trace.status === "failed") {
      findings.push({
        severity: "error",
        message: `Falló la traza de ${timeLabel(trace)}${trace.error ? `: ${trace.error.message}` : ""}`,
      });
    }
    if (trace.status === "started") {
      findings.push({
        severity: "warning",
        message: `La traza de ${timeLabel(trace)} nunca terminó`,
      });
    }
    if (hasIntraTraceRegression(trace)) {
      findings.push({
        severity: "error",
        message: `El progreso bajó dentro de la misma traza de ${timeLabel(trace)}`,
      });
    }
    if (hasInterTraceRegression(traces, index)) {
      const previous = findPreviousTraceForSameItem(traces, index);
      findings.push({
        severity: "error",
        message: `El progreso bajó entre la traza de ${previous ? timeLabel(previous) : "?"} y la de ${timeLabel(trace)}`,
      });
    }
    if (trace.flags?.topicFinishRejected) {
      findings.push({
        severity: "warning",
        message: `El servidor no confirmó terminar el tópico en la traza de ${timeLabel(trace)} (lecciones o evaluación sin verificar)`,
      });
    }
    if (trace.flags?.courseFinishRejected) {
      findings.push({
        severity: "warning",
        message: `El servidor no confirmó terminar el curso en la traza de ${timeLabel(trace)} (tópicos o examen sin verificar)`,
      });
    }
  });

  return findings;
};
